/**
 * Redux slice for managing user-related state.
 *
 * Manages user data and user preferences. Includes actions for setting user data,
 * updating preferences, and resetting the state.
 *
 * @module userSlice
 */

import { createSlice } from "@reduxjs/toolkit";

/**
 * @typedef {Object} UserState
 * @property {Object} userData - Contains information about the logged-in user.
 * @property {Array} preferences - List of user preferences such as journal selections.
 */

/** @type {UserState} */
const initialState = {
  userData: {},
  preferences: [],
  dataSyncTime: {}
};

/**
 * Creates a user slice with reducers to manage user data and preferences.
 */
export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    /**
     * Sets the user data after successful login or update.
     * @param {UserState} state - The current state of the user slice.
     * @param {Object} action - Redux action object.
     * @param {Object} action.payload - User data object.
     */
    setUser: (state, action) => {
      state.userData = action?.payload;
    },

    /**
     * Sets the user's preferences (e.g., selected journals).
     * @param {UserState} state - The current state of the user slice.
     * @param {Object} action - Redux action object.
     * @param {Array} action.payload - An array of user preferences.
     */
    setPreferences: (state, action) => {
      state.preferences = action?.payload;
    },

    setDataSyncTime: (state, action) => {
        state.dataSyncTime = action?.payload
    },

    /**
     * Clears all user-related state (e.g., on logout).
     * @returns {UserState} - Resets to initial state.
     */
    clearUser: () => initialState,
    clearUserData: (state) => {
      state.userData = {};
    },
  },
});

// Export actions
export const { setUser, setPreferences, clearUser, clearUserData, setDataSyncTime } = userSlice.actions;

// Export reducer
export default userSlice.reducer;
